import { EmptySearchPageError } from '../common/errors/EmptySearchPageError.js'
import { delay } from '../common/delay.js'
import { contactsExportType } from '../const/contactsExportType.js'
import { ExportMetadataFactory } from '../export/exportMetadataFactory.js'
import { utils } from '../common/utils.js'
import { getContactsFromSearchPage } from '../linkedInExportApi.js'
import type {
	ContactsIteratorResult,
	ExportMetadataItem,
	IBroker
} from './types.js'

interface SearchRequest {
	parameters: Record<string, string>
	start: number
}

export class LinkedInSearchPageContactsIterator {
	readonly #broker: IBroker
	#canNext = true
	readonly #searchRequest: SearchRequest
	#total: number | undefined
	readonly #pageSize = 10
	readonly #searchPageUri: URL
	readonly #metadata: ExportMetadataItem[]

	constructor(broker: IBroker, uri: URL) {
		this.#broker = broker
		const searchParameters = ExportMetadataFactory.getSearchParamsDictionary(
			uri.search
		) as Record<string, string>
		this.#metadata = ExportMetadataFactory.create(uri)
		this.#searchRequest = this.#parseSearchRequest(searchParameters)
		this.#searchPageUri = uri
	}

	async getNext(metadata?: object): Promise<ContactsIteratorResult> {
		const isFirstIteration = this.#total === undefined
		const referrer = utils.getReferrer(this.#searchPageUri)
		type ResponseShape = {
			paging: { total: number; start: number; count: number }
			contacts: unknown[]
		}
		let response: ResponseShape | null = null
		let attempts = 0
		do {
			response = (await getContactsFromSearchPage(
				this.#broker,
				this.#searchRequest,
				referrer,
				metadata
			)) as ResponseShape
			// sleep a bit
			if (!isFirstIteration && response.paging.total === 0) {
				await delay.randomWaitAsync(8, 30)
			}
			attempts += 1
		} while (!isFirstIteration && response.paging.total === 0 && attempts <= 5)

		const isNotAllContactsProcessed =
			response.paging.total > response.paging.start + response.paging.count
		this.#canNext = isNotAllContactsProcessed
		if (this.#canNext) {
			this.#searchRequest.start += this.#pageSize
		}

		if (this.#total === undefined) {
			if (response.paging.total === 0) {
				throw new EmptySearchPageError(
					`there is no contacts on the search page ${this.#searchPageUri}`,
					metadata
				)
			}
			this.#total = response.paging.total
		}

		const result = {
			found: response.contacts,
			processed: isNotAllContactsProcessed
				? response.paging.count
				: response.paging.total - response.paging.start
		}
		return result
	}

	canMove(): boolean {
		return this.#canNext
	}

	getTotal(): number {
		return this.#total ?? 0
	}

	getMetadata(): ExportMetadataItem[] {
		return this.#metadata
	}

	getType(): string {
		return contactsExportType.linkedInContactsSearchPage
	}

	#parseSearchRequest(queryParameters: Record<string, string>): SearchRequest {
		let start = 0
		if (queryParameters.page) {
			const originalPageNumber = queryParameters.page
			const pageNumber = parseInt(originalPageNumber)
			start = (pageNumber - 1) * this.#pageSize
			delete queryParameters.page
		}

		const result = {
			parameters: queryParameters,
			start: start
		}
		return result
	}
}
